<!-- View Employee Details Modal -->
<template>
  <BaseModal
    :is-visible="isVisible"
    title="View Employee Details"
    :subtitle="`${employee?.first_name || 'Employee'}'s Information`"
    :icon="User"
    size="2xl"
    type="default"
    :show-footer="true"
    @close="closeModal"
  >

    <!-- Top Section: Profile Pic & Status + Personal Info -->
    <div class="p-6 border-b border-gray-200 dark:border-gray-700 flex flex-col items-center gap-4 md:flex-row md:gap-8 md:items-center md:justify-center">
      <!-- Profile Pic and Status -->
      <div class="flex flex-col items-center justify-center">
        <div class="relative">
          <div class="h-32 w-32 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center overflow-hidden">
            <img
              v-if="employee?.user_image && !imageErrored"
              :src="employee.user_image"
              :alt="`${employee.first_name} ${employee.last_name}`"
              class="h-full w-full object-cover"
              @error="handleImageError"
            />
            <span v-else class="text-gray-500 dark:text-gray-200 text-4xl font-medium">
              {{ initials || (employee?.first_name?.charAt(0) || '') + (employee?.last_name?.charAt(0) || '') }}
            </span>
          </div>
          <span
            :class="[
              'absolute bottom-1 right-4 block h-6 w-6 rounded-full ring-2 ring-white',
              employee?.status === 'Active' ? 'bg-green-500' : 'bg-red-500'
            ]"
            aria-hidden="true"
          ></span>
        </div>
      </div>
      <!-- Personal Info -->
      <div class="flex flex-col items-center md:items-start justify-center flex-1">
        <span class="block text-base font-semibold text-gray-700 dark:text-gray-300 mb-2">First Name</span>
        <div class="border border-gray-300 dark:border-gray-600 rounded-xl px-4 py-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-2xl font-bold w-full text-center md:text-left">
          {{ employee?.first_name || '' }}
        </div>
      </div>
    </div>

    <!-- Personal Information Section -->
    <div class="p-4">
      <div class="font-bold text-lg mb-4 dark:text-white">Personal Information</div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-1">Last Name</label>
          <div class="border border-gray-300 dark:border-gray-600 rounded-xl px-4 py-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
            {{ employee?.last_name || 'N/A' }}
          </div>
        </div>
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-1">Middle Name</label>
          <div class="border border-gray-300 dark:border-gray-600 rounded-xl px-4 py-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
            {{ employee?.middle_name || 'N/A' }}
          </div>
        </div>
        <div class="md:col-span-2">
          <label class="block text-sm font-semibold text-gray-700 mb-1">Email Address</label>
          <div class="border border-gray-300 dark:border-gray-600 rounded-xl px-4 py-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
            {{ employee?.email || 'N/A' }}
          </div>
        </div>
      </div>
    </div>

    <!-- Contact Information Section -->
    <div class="p-6">
      <div class="font-bold text-lg mb-4 dark:text-white">Contact Information</div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Phone Number</label>
          <div class="border border-gray-300 dark:border-gray-600 rounded-xl px-4 py-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
            {{ employee?.phone_number || 'N/A' }}
          </div>
        </div>
        <div>
          <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">City</label>
          <div class="border border-gray-300 dark:border-gray-600 rounded-xl px-4 py-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
            {{ employee?.city || 'N/A' }}
          </div>
        </div>
        <div>
          <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">State</label>
          <div class="border border-gray-300 dark:border-gray-600 rounded-xl px-4 py-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
            {{ employee?.state || 'N/A' }}
          </div>
        </div>
        <div>
          <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">ZIP Code</label>
          <div class="border border-gray-300 dark:border-gray-600 rounded-xl px-4 py-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
            {{ employee?.zip_code || 'N/A' }}
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <BaseButton
        text="Close"
        variant="primary"
        @click="closeModal"
      />
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { User } from 'lucide-vue-next'
import { watch, ref, computed } from 'vue'
import BaseModal from '@/components/common/BaseModal.vue'
import BaseButton from '@/components/common/BaseButton.vue'

const props = defineProps({
  isVisible: {
    type: Boolean,
    required: true
  },
  employee: {
    type: Object,
    required: false,
    default: null
  }
})


const emit = defineEmits(['close', 'image-error'])

// local flag to show placeholder if image fails to load
const imageErrored = ref(false)

const handleImageError = (event: Event) => {
  imageErrored.value = true
  emit('image-error', event)
}

const closeModal = () => {
  emit('close')
}

// Reset image error flag when employee changes
watch(
  () => props.employee,
  () => {
    imageErrored.value = false
  }
)

// Compute initials from available name fields (handles single-field full names)
const initials = computed(() => {
  const first = props.employee?.first_name?.toString().trim() || ''
  const last = props.employee?.last_name?.toString().trim() || ''

  if (first && last) {
    return (first.charAt(0) + last.charAt(0)).toUpperCase()
  }

  const nameSource = (first || props.employee?.email || '') as string
  if (nameSource) {
    const parts = nameSource.split(/\s+/).filter(Boolean)
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase()
    }
    return parts[0].charAt(0).toUpperCase()
  }

  return ''
})
</script>

<style scoped>
/* Any component-specific styles can go here */
/* BaseModal handles all the modal transitions and basic styling */
</style>
